import { Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/v2/accordion";
import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/v2/drawer";
import { Label } from "@/components/ui/v2/label";
import { Separator } from "@/components/ui/v2/separator";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { URL_PARAMS } from "@/constants/urlParams";
import { useClientFilters } from "@/features/clients/api/useClientFilters";
import { useClientCount } from "@/lib/contexts/Client/Count/useClientCount";
import { FilterOption } from "@/types/FilterOption";
import { ClientFilter } from "@/types/response/ClientType";

type ClientsFiltersDrawerProps = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchParams: URLSearchParams;
  tenantId: string | null;
  searchValue?: string;
}>;

type FilterSectionProps = {
  value: string;
  title: string;
  options: FilterOption<string>[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
};

function FilterSection({
  value,
  title,
  options,
  selectedValues,
  onSelectionChange,
}: FilterSectionProps) {
  const { t } = useTranslation();

  const handleToggle = (optionValue: string) => {
    if (selectedValues.includes(optionValue)) {
      onSelectionChange(selectedValues.filter((v) => v !== optionValue));
    } else {
      onSelectionChange([...selectedValues, optionValue]);
    }
  };

  return (
    <AccordionItem value={value} className="border rounded-lg last:border-b">
      <AccordionTrigger className="px-4">{t(title)}</AccordionTrigger>
      <AccordionContent className="px-4">
        <div className="space-y-2 max-h-64 overflow-y-auto overflow-x-hidden -mx-2 px-2">
          {options.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              {t("commonSentences.noOptionsFound")}
            </p>
          ) : (
            options.map((option) => (
              <Label
                key={option.value}
                className="flex items-center gap-2 cursor-pointer hover-tint-primary transition-colors rounded-md p-2 -mx-2"
              >
                <Checkbox
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={() => handleToggle(option.value)}
                />
                <span className="text-sm flex-1">{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-xs text-muted-foreground">
                    ({option.count})
                  </span>
                )}
              </Label>
            ))
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export function ClientsFiltersDrawer({
  open,
  onOpenChange,
  searchParams,
  tenantId,
  searchValue,
}: ClientsFiltersDrawerProps) {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();

  const getFiltersFromParams = useCallback(
    (): ClientFilter => ({
      clientType: searchParams.getAll("clientType"),
      paymentType: searchParams.getAll("paymentType"),
    }),
    [searchParams]
  );

  const [currentFilters, setCurrentFilters] =
    useState<ClientFilter>(getFiltersFromParams);

  useEffect(() => {
    setCurrentFilters(getFiltersFromParams());
  }, [searchParams, open, getFiltersFromParams]);

  const { data: filters, isLoading: filtersLoading } = useClientFilters({
    tenantId,
    search: searchValue,
  });

  const { countByFilter, countByFilterLoading } = useClientCount({
    filter: currentFilters,
    search: searchValue,
  });

  const handleApplyFilters = () => {
    const newSearchParams = new URLSearchParams();

    Object.entries(currentFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          newSearchParams.append(key, value.toString());
        });
      }
    });

    newSearchParams.set(URL_PARAMS.PAGE, "1");
    if (searchValue) {
      newSearchParams.set(URL_PARAMS.SEARCH, searchValue);
    }

    // Preserve sort parameters if they exist
    const orderBy = searchParams.get(URL_PARAMS.ORDER_BY);
    const descending = searchParams.get(URL_PARAMS.DESCENDING);
    if (orderBy) newSearchParams.set(URL_PARAMS.ORDER_BY, orderBy);
    if (descending) newSearchParams.set(URL_PARAMS.DESCENDING, descending);

    setSearchParams(newSearchParams);
    onOpenChange(false);
  };

  const handleClearFilters = () => {
    const defaultFilters: ClientFilter = {
      clientType: [],
      paymentType: [],
    };
    setCurrentFilters(defaultFilters);

    const newSearchParams = new URLSearchParams(searchParams);
    Object.keys(defaultFilters).forEach((key) => newSearchParams.delete(key));
    setSearchParams(newSearchParams);
  };

  const handleFilterChange = (key: keyof ClientFilter, values: string[]) => {
    setCurrentFilters((prev) => ({
      ...prev,
      [key]: values,
    }));
  };

  const entityName = t("common.clients");

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            {t("admin.filters.filterTitle", { entity: entityName })}
          </DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col gap-4 overflow-y-auto px-4">
          {/* Total Records */}
          <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
            <span className="text-sm font-medium">
              {t("admin.filters.totalRecords", {
                count: countByFilter?.count ?? 0,
                entity: entityName,
              })}
            </span>
            {countByFilterLoading && <Skeleton className="h-4 w-16" />}
          </div>

          {/* Search Tag */}
          {searchValue && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1.5">
                <Search className="h-3 w-3" />
                <span className="text-xs">"{searchValue}"</span>
              </Badge>
            </div>
          )}

          <Separator />

          {/* Filter Sections */}
          {filtersLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <Accordion
              type="multiple"
              defaultValue={[
                ...(filters?.clientType && filters.clientType.length > 0
                  ? ["clientType"]
                  : []),
                ...(filters?.paymentType && filters.paymentType.length > 0
                  ? ["paymentType"]
                  : []),
              ]}
              className="w-full flex flex-col gap-4 pb-4"
            >
              {filters?.clientType && filters.clientType.length > 0 && (
                <FilterSection
                  value="clientType"
                  title="common.clientType"
                  options={filters.clientType}
                  selectedValues={currentFilters.clientType || []}
                  onSelectionChange={(values) =>
                    handleFilterChange("clientType", values)
                  }
                />
              )}
              {filters?.paymentType && filters.paymentType.length > 0 && (
                <FilterSection
                  value="paymentType"
                  title="common.paymentType"
                  options={filters.paymentType}
                  selectedValues={currentFilters.paymentType || []}
                  onSelectionChange={(values) =>
                    handleFilterChange("paymentType", values)
                  }
                />
              )}
            </Accordion>
          )}
        </div>

        <DrawerFooter className="w-full md:flex-row gap-2">
          <Button onClick={handleApplyFilters} className="md:w-1/2">
            {t("admin.filters.showFilterdData", { entity: entityName })}
          </Button>
          <Button
            variant="ghost"
            onClick={handleClearFilters}
            className="md:w-1/2"
          >
            <Trash2 className="h-4 w-4" />
            {t("admin.filters.resetAllFilters")}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
